/**
 * CookieBanner — DPDPA-compliant first-visit consent banner.
 *
 * Defaults to "essential cookies only" (consent NOT inferred). User must
 * explicitly Accept-All, Reject-All, or Customize. Choice is stored in
 * localStorage; for logged-in users, also mirrored server-side via POST
 * /api/users/me/consent for auditable trail.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Check, X, Settings } from 'lucide-react';
import axios from 'axios';
import { updateConsent } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONSENT_KEY = 'ibclub.consent.v1';

const defaultConsent = { essential: true, analytics: false, marketing: false };

const readConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const writeConsent = (consent) => {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({
      ...consent,
      essential: true,  // can't be turned off
      recorded_at: new Date().toISOString(),
      policy_version: '1.0',
    }));
  } catch { /* localStorage might be disabled */ }
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [choice, setChoice] = useState(defaultConsent);

  useEffect(() => {
    if (!readConsent()) {
      // Delay so it doesn't block other onboarding popups
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // When visible (collapsed mode), pad the body by a thin margin so the
  // floating card doesn't visually overlap critical bottom-bar content.
  // When customizing (modal-mode), we centre it and dim the page below.
  useEffect(() => {
    if (visible && !customizing) {
      const prev = document.body.style.paddingBottom;
      document.body.style.paddingBottom = '60px';
      return () => { document.body.style.paddingBottom = prev; };
    }
  }, [visible, customizing]);

  const save = async (consent) => {
    writeConsent(consent);
    setVisible(false);
    // Flip GA4 consent state immediately so the next page_view fires correctly.
    updateConsent({ analytics: !!consent.analytics, marketing: !!consent.marketing });
    // Mirror to server if logged in (silently fail otherwise)
    try {
      await axios.post(`${API}/users/me/consent`, consent, { withCredentials: true });
    } catch { /* not authed — that's fine */ }
  };

  const acceptAll = () => save({ essential: true, analytics: true, marketing: true });
  const rejectAll = () => save({ essential: true, analytics: false, marketing: false });
  const saveCustom = () => save({ essential: true, analytics: choice.analytics, marketing: choice.marketing });

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          className={
            customizing
              ? "fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
              : "fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 z-[90] md:max-w-md"
          }
          data-testid="cookie-banner"
        >
          <div className="bg-slate-900 border border-[#d4af37]/30 shadow-2xl backdrop-blur-md p-5 w-full md:max-w-md">
            {!customizing ? (
              <div>
                <div className="flex items-start gap-3 mb-3">
                  <Cookie className="text-[#d4af37] flex-shrink-0 mt-0.5" size={20} />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-sm mb-1">
                      Your privacy matters
                    </h3>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      We use cookies for essential functionality + optional analytics. Per India&apos;s DPDP Act 2023, you choose what&apos;s stored.{' '}
                      <a href="/privacy-policy" className="text-[#d4af37] hover:underline">Privacy Policy</a>.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={acceptAll}
                    data-testid="cookie-accept-all"
                    className="text-xs bg-[#d4af37] text-slate-950 px-3.5 py-2 font-semibold hover:bg-[#e5c558] transition-colors flex-1"
                  >
                    Accept all
                  </button>
                  <button
                    onClick={rejectAll}
                    data-testid="cookie-reject-all"
                    className="text-xs text-slate-300 hover:text-white border border-white/20 px-3 py-2 transition-colors"
                  >
                    Essential only
                  </button>
                  <button
                    onClick={() => setCustomizing(true)}
                    data-testid="cookie-customize"
                    className="text-xs text-slate-400 hover:text-white border border-white/10 px-2.5 py-2 inline-flex items-center gap-1"
                    aria-label="Customize"
                  >
                    <Settings size={12} />
                  </button>
                </div>
              </div>
            ) : (
              <div data-testid="cookie-customize-panel">
                <div className="flex items-center gap-2 mb-4">
                  <Settings className="text-[#d4af37]" size={18} />
                  <h3 className="text-white font-semibold">Cookie preferences</h3>
                </div>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3 opacity-60">
                    <div className="w-10 h-5 bg-[#d4af37] rounded-full relative flex-shrink-0">
                      <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-slate-950 rounded-full" />
                    </div>
                    <div className="text-sm">
                      <p className="text-white font-semibold">Essential</p>
                      <p className="text-xs text-slate-500">Auth session, security, accessibility. Cannot be disabled.</p>
                    </div>
                  </li>
                  <Toggle
                    label="Analytics"
                    description="Anonymized usage stats so we can improve the product."
                    enabled={choice.analytics}
                    onChange={(v) => setChoice((s) => ({ ...s, analytics: v }))}
                    testId="toggle-analytics"
                  />
                  <Toggle
                    label="Marketing"
                    description="Personalized event invites + recommendation emails."
                    enabled={choice.marketing}
                    onChange={(v) => setChoice((s) => ({ ...s, marketing: v }))}
                    testId="toggle-marketing"
                  />
                </ul>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setCustomizing(false)}
                    className="text-xs text-slate-400 hover:text-white border border-white/20 px-4 py-2"
                  >
                    Back
                  </button>
                  <button
                    onClick={saveCustom}
                    data-testid="cookie-save-preferences"
                    className="text-xs bg-[#d4af37] text-slate-950 px-4 py-2 font-semibold hover:bg-[#e5c558]"
                  >
                    Save preferences
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Toggle = ({ label, description, enabled, onChange, testId }) => (
  <li className="flex items-start gap-3">
    <button
      onClick={() => onChange(!enabled)}
      data-testid={testId}
      aria-label={`${enabled ? 'Disable' : 'Enable'} ${label}`}
      className={`w-10 h-5 ${enabled ? 'bg-[#d4af37]' : 'bg-slate-700'} rounded-full relative flex-shrink-0 transition-colors`}
    >
      <span
        className={`absolute top-0.5 w-4 h-4 bg-slate-950 rounded-full transition-transform ${enabled ? 'right-0.5' : 'left-0.5'}`}
      />
    </button>
    <div className="text-sm flex-1">
      <p className="text-white font-semibold">{label}</p>
      <p className="text-xs text-slate-500">{description}</p>
    </div>
  </li>
);

export default CookieBanner;
